import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { subscribeToPush } from '../utils/pushNotification';
import { getPostLoginRedirect, redirectAfterLogin } from '../utils/authRedirect';

/**
 * Auth Context - HttpOnly Cookie tabanlı
 * Token'lar JS tarafından görünmez, backend yönetir.
 */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [favorites, setFavorites] = useState([]);

  useEffect(() => {
    checkAuth();

    // PWA veya tarayıcı arka plandan ön plana geldiğinde oturumu ve bildirimi canlı tut
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkAuth();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      const redirect = getPostLoginRedirect();
      if (redirect) {
        redirectAfterLogin(redirect);
      }
    }
  }, [loading, isAuthenticated]);

  useEffect(() => {
    const handleUrlChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('logout') === 'true') {
        checkAuth();
      }
      if (urlParams.get('login') === 'true') {
        setIsLoginOpen(true);
      }
    };
    window.addEventListener('popstate', handleUrlChange);
    handleUrlChange();
    return () => window.removeEventListener('popstate', handleUrlChange);
  }, []);

  useEffect(() => {
    const handleApiLogout = () => {
      setUser(null);
      setIsAuthenticated(false);
      setFavorites([]);
    };
    window.addEventListener('auth:logout', handleApiLogout);
    return () => window.removeEventListener('auth:logout', handleApiLogout);
  }, []);

  const checkAuth = async () => {
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get('logout') === 'true') {
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    if (urlParams.get('from') === 'member-panel') {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    try {
      const response = await api.getProfile();
      const userData = response.data || response;
      setUser(userData);
      setIsAuthenticated(true);
      loadFavorites();
      
      // Üye bilgilerini localStorage'a kaydet (abandoned cart bildirimi için)
      if (userData?.email) {
        localStorage.setItem('userEmail', userData.email);
      }
      if (userData?.id) {
        localStorage.setItem('userId', userData.id);
      }
      
      // Giriş yapılmışsa ve bildirimler açıksa, aboneliği kullanıcı bilgileriyle (ID, email, rol) güncelle
      if ('Notification' in window && Notification.permission === 'granted') {
        subscribeToPush({ role: userData?.role || 'user', userId: userData?.id, email: userData?.email }).catch(() => {});
      }
    } catch {
      // Giriş yapılmamış — normal durum, konsola hata yazdırma
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  const loadFavorites = async () => {
    try {
      const response = await api.getFavorites();
      const favoritesData = response.data || response;
      setFavorites(Array.isArray(favoritesData) ? favoritesData : []);
    } catch {
      setFavorites([]);
    }
  };

  const toggleFavorite = async (productId) => {
    if (!isAuthenticated) {
      setIsLoginOpen(true);
      return null; // null = login gerekli, toast gösterme
    }
    try {
      const existingFavorite = favorites.find(f => String(f.productId) === String(productId));
      if (existingFavorite) {
        await api.removeFavorite(existingFavorite.id);
        setFavorites(favorites.filter(f => f.id !== existingFavorite.id));
        return false; // false = favorilerden çıkarıldı
      } else {
        const response = await api.addFavorite(productId);
        const newFavorite = response.data || response;
        setFavorites([...favorites, newFavorite]);
        return true; // true = favorilere eklendi
      }
    } catch (error) {
      throw error;
    }
  };

  const isFavorite = (productId) => {
    if (!favorites || favorites.length === 0) return false;
    return favorites.some(f => String(f.productId) === String(productId));
  };

  const login = async (email, password, rememberMe = true) => {
    try {
      // rememberMe backend'e gönderiliyor - cookie süresini backend belirliyor (varsayılan 90 gün)
      const response = await api.login({ email, password, rememberMe });
      const userData = response.data?.user || response.user;
      setUser(userData);
      setIsAuthenticated(true);
      setIsLoginOpen(false);
      loadFavorites();
      // Üye bilgilerini localStorage'a kaydet (abandoned cart bildirimi için)
      if (userData?.email) {
        localStorage.setItem('userEmail', userData.email);
      }
      if (userData?.id) {
        localStorage.setItem('userId', userData.id);
      }
      // Giriş yapıldıysa ve bildirimler açıksa, aboneliği kullanıcı bilgileriyle (ID, email, rol) güncelle
      if ('Notification' in window && Notification.permission === 'granted') {
        subscribeToPush({ role: userData?.role || 'user', userId: userData?.id, email: userData?.email }).catch(() => {});
      }

      const redirect = getPostLoginRedirect();
      if (redirect) {
        redirectAfterLogin(redirect);
      }
      
      return { success: true };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error?.message || error.response?.data?.message || 'Giriş başarısız',
      };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.register(userData);
      return { success: true, message: response.message || 'Kayıt başarılı' };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error?.message || error.response?.data?.message || 'Kayıt başarısız',
      };
    }
  };

  const forgotPassword = async (email, recaptchaToken = null) => {
    try {
      const response = await api.forgotPassword({ email, recaptchaToken });
      return { success: true, message: response.message || 'Şifre sıfırlama bağlantısı gönderildi.' };
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error?.message || error.response?.data?.message || 'İşlem başarısız',
      };
    }
  };

  const checkAvailability = async (field, value) => {
    try {
      const response = await api.checkAvailability(field, value);
      return { success: true, data: response.data };
    } catch (error) {
      const message = error.response?.data?.message || error.response?.data?.error?.message || error.message || 'Kontrol edilemedi';
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await api.logout(); // Backend cookie'leri temizler
    } catch {
      // Sessizce devam et
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      setFavorites([]);
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userId');
      sessionStorage.clear();
      window.location.href = '/?logout=true';
    }
  };

  const updateUser = (userData) => {
    setUser((prev) => ({ ...prev, ...userData }));
  };

  const value = useMemo(() => ({
    user, loading, isAuthenticated, isLoginOpen, setIsLoginOpen,
    favorites, login, register, checkAvailability, forgotPassword,
    logout, updateUser, checkAuth, toggleFavorite, isFavorite, loadFavorites,
  }), [user, loading, isAuthenticated, isLoginOpen, favorites]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
